import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";

/**
 * BidLeadPage
 *
 * Flow:
 * 1. Vendor lands here from Lead Details
 * 2. We check:
 *    - lead availability
 *    - vendor payment verification status
 * 3. If payment verified → allow bidding
 * 4. If not → require Shopify $1 authorization (refunded)
 */
export default function BidLeadPage() {
  const { estimateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const token = localStorage.getItem("vendor_token");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [lead, setLead] = useState(null);
  const [hasVerifiedPayment, setHasVerifiedPayment] = useState(false);

  const [bidAmount, setBidAmount] = useState("");
  const [submittingBid, setSubmittingBid] = useState(false);

  const [verifyingPayment, setVerifyingPayment] = useState(false);

  /**
   * Detect Shopify redirect
   * Example:
   * /bid-lead/:estimateId?payment_verified=true
   */
  const paymentVerifiedFromRedirect =
    new URLSearchParams(location.search).get("payment_verified") === "true";

  // --------------------------------------------------
  // Initial load
  // --------------------------------------------------
  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    async function loadPage() {
      try {
        setLoading(true);

        const leadResponse = await fetch(
          `https://api.showmeyourjunk.biz/api/vendor/lead/${estimateId}`,
          {
            headers: { "x-vendor-token": token }
          }
        );

        const paymentStatusResponse = await fetch(
          "https://api.showmeyourjunk.biz/api/vendor/payment-status",
          {
            headers: { "x-vendor-token": token }
          }
        );

        if (!leadResponse.ok || !paymentStatusResponse.ok) {
          throw new Error("Failed to load bid page data");
        }

        const leadData = await leadResponse.json();
        const paymentStatus = await paymentStatusResponse.json();

        if (!leadData.success) {
          throw new Error(leadData.error || "Lead unavailable");
        }

        setLead(leadData.lead);
        setHasVerifiedPayment(!!paymentStatus.has_verified_payment);
      } catch (err) {
        console.error(err);
        setError("Unable to load lead details.");
      } finally {
        setLoading(false);
      }
    }

    loadPage();
  }, [estimateId, token, navigate]);

  // --------------------------------------------------
  // Refresh payment status after Shopify redirect
  // --------------------------------------------------
  useEffect(() => {
    if (!paymentVerifiedFromRedirect || !token) return;

    async function refreshPaymentStatus() {
      try {
        const res = await fetch(
          "https://api.showmeyourjunk.biz/api/vendor/payment-status",
          { headers: { "x-vendor-token": token } }
        );

        if (!res.ok) return;

        const data = await res.json();
        setHasVerifiedPayment(!!data.has_verified_payment);
      } catch (err) {
        console.error("Failed to refresh payment status", err);
      }
    }

    refreshPaymentStatus();
  }, [paymentVerifiedFromRedirect, token]);

  // --------------------------------------------------
  // Start payment verification ($1 AUTHORIZATION)
  // --------------------------------------------------
  async function handleVerifyPayment() {
    // 🔒 Prevent re-verification once verified
    if (hasVerifiedPayment) return;

    try {
      setVerifyingPayment(true);

      const res = await fetch(
        "https://api.showmeyourjunk.biz/api/vendor/verify-payment-method",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-vendor-token": token
          }
        }
      );

      if (!res.ok) {
        throw new Error("Failed to start payment verification");
      }

      const data = await res.json();

      // Already verified → no redirect needed
      if (data.has_verified_payment || data.verified) {
        setHasVerifiedPayment(true);
        setVerifyingPayment(false);
        return;
      }

      if (data.redirect_url) {
        window.location.href = data.redirect_url;
        return;
      }

      throw new Error("Unexpected payment verification response");

    } catch (err) {
      console.error(err);
      alert("Unable to verify payment method.");
      setVerifyingPayment(false);
    }
  }

  // --------------------------------------------------
  // Submit bid
  // --------------------------------------------------
  async function handleSubmitBid() {
    if (!bidAmount || !hasVerifiedPayment) return;

    try {
      setSubmittingBid(true);

      const res = await fetch(
        "https://api.showmeyourjunk.biz/api/bids",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-vendor-token": token
          },
          body: JSON.stringify({
            estimate_id: estimateId,
            bid_amount: bidAmount
          })
        }
      );

      if (!res.ok) {
        throw new Error("Failed to submit bid");
      }

      alert("Bid submitted successfully!");
      navigate("/dashboard");

    } catch (err) {
      console.error(err);
      alert("Unable to submit bid.");
    } finally {
      setSubmittingBid(false);
    }
  }

  // --------------------------------------------------
  // Render
  // --------------------------------------------------
  if (loading) return <div style={{ padding: 40 }}>Loading bid details…</div>;
  if (error) return <div style={{ padding: 40 }}>{error}</div>;
  if (!lead) return <div style={{ padding: 40 }}>Lead not found.</div>;

  return (
    <div style={{ padding: 24 }}>
      <h1>
        Bid on This Lead
        {hasVerifiedPayment && (
          <span
            style={{
              marginLeft: 12,
              padding: "4px 8px",
              background: "#2ecc71",
              color: "#fff",
              borderRadius: 6,
              fontSize: 12,
              verticalAlign: "middle"
            }}
          >
            ✔ Payment Verified
          </span>
        )}
      </h1>

      <p>
        You are placing a bid to be awarded this lead.
        You will only be charged if your bid is accepted.
      </p>

      {!hasVerifiedPayment && (
        <section>
          <h3>Verify Payment Method</h3>
          <p>
            To place bids, we perform a temporary <strong>$1 authorization</strong>{" "}
            on your card to verify it. The charge is immediately refunded.
            You will only be charged if you win the job.
          </p>

          <button
            onClick={handleVerifyPayment}
            disabled={verifyingPayment}
          >
            {verifyingPayment ? "Verifying…" : "Verify Payment Method"}
          </button>
        </section>
      )}

      {hasVerifiedPayment && (
        <section>
          <h3>Place Your Bid</h3>

          <input
            type="number"
            min="1"
            step="0.01"
            placeholder="Bid amount"
            value={bidAmount}
            onChange={(e) => setBidAmount(e.target.value)}
          />

          <button
            onClick={handleSubmitBid}
            disabled={submittingBid || !bidAmount}
          >
            {submittingBid ? "Submitting…" : "Submit Bid"}
          </button>
        </section>
      )}
    </div>
  );
}
